export type PackagingPromptInput = {
  businessName: string;
  slug: string;
  priceDollars?: number;
  previewAddress?: string;
  sourcePath?: string;
};

export function createPackagingPrompt(input: PackagingPromptInput) {
  const businessName = input.businessName.trim() || '[BUSINESS NAME]';
  const slug = input.slug.trim() || '[business-name]';
  const proposalPrice = Number.isFinite(input.priceDollars) && (input.priceDollars ?? -1) >= 0
    ? (input.priceDollars ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    : '[PROPOSAL PRICE]';
  const previewAddress = input.previewAddress?.trim() || `${slug}.sites.example.com`;
  const sourcePath = input.sourcePath?.trim() || '[USE THE CURRENT WORKSPACE]';
  return `Package this finished website for import into my SiteSales prospect platform.

Website/project name: ${businessName}
Prospect slug: ${slug}
Proposal price: ${proposalPrice}
Preview address: ${previewAddress}
Source project or finished-site folder: ${sourcePath}

Create a self-contained ZIP with these requirements:

1. Identify the customer-facing production website.
   - If it is static, package its finished HTML, CSS, JavaScript, fonts, images, videos, and other required assets.
   - If it requires a build, run the existing production build and package only the resulting build output.
   - Do not redesign, regenerate, or materially change the website.

2. Make the ZIP directly importable.
   - index.html must be at the root of the ZIP.
   - Preserve all required relative asset paths.
   - Ensure the site works when served from its ZIP root.
   - Do not include absolute local filesystem paths.
   - Exclude node_modules, source-only files, raw media, render workspaces, caches, temporary files, secrets, environment files, logs, previous versions, backups, and unused assets.
   - Never include API keys, credentials, .env files, or private configuration.

3. Preserve the complete visitor experience.
   - Desktop and mobile layouts
   - Navigation and hamburger menus
   - Buttons and links
   - Scroll effects and animations
   - Video and audio assets
   - Forms where they can operate safely in a preview
   - Accessibility behavior
   - Keep scene copy and CTA treatments consistent, and position them so they do not obscure the scene's focal subject, brand mark, signage, or artwork at either proposal-preview width.

   Apply the Auri79 prospect compatibility profile:
   - On actual mobile devices, do not run duplicate hidden copies of expensive media or animation experiences.
   - Any guided autoplay must derive its position from absolute elapsed time, not accumulated or capped animation-frame deltas.
   - For a mobile guided tour driven by a scrubbed master video, wait for video metadata, use native video playback as the authoritative clock, and derive scroll position from video.currentTime. Do not advance scroll on a wall clock while separately seeking the same video.
   - Encode the mobile guided-tour master to approximately the intended tour duration so native playback can stay near 1x speed; do not depend on unsupported or unstable high playback rates to keep the tour on time.
   - When the proposal shows desktop and phone previews together, publish guided-tour play/pause state and exact world progress continuously to the proposal shell. The companion must render that leader progress without starting a second scroll clock or echoing mirrored scroll events back to the leader.
   - Any frame-smoothed video seeking must use elapsed-time-based easing, not a fixed percentage per rendered frame.
   - Assign seekable media URLs directly to video elements so the browser can use HTTP byte ranges; do not fetch an entire large video into a Blob.
   - For scroll-scrubbed video, include a mobile H.264 variant around 720 px wide, 24–30 FPS, no audio, faststart, and a short GOP of approximately four frames. Wire the mobile source into the site configuration.
   - Respect prefers-reduced-motion while allowing an explicit visitor opt-in to motion.

4. Add prospect-manifest.json at the ZIP root containing:
   - formatVersion: 1
   - businessName: ${businessName}
   - slug: ${slug}
   - proposalPrice: ${proposalPrice}
   - previewAddress: ${previewAddress}
   - entryPoint: index.html
   - createdAt
   - siteType: static
   - assetPolicy: proposal-only
   - a list of included files with sizes and SHA-256 hashes
   - external domains the website depends on
   - features that will not work without an external service
   - compatibilityProfile: auri79-prospect-v1

5. Validate the package before delivering it.
   - Serve the packaged directory locally.
   - Test desktop and mobile versions.
   - Test guided autoplay under mobile CPU pressure and confirm its duration does not change materially when frames are dropped.
   - Confirm the mobile master-video duration closely matches the intended guided-tour duration.
   - If the site uses video scrubbing, verify a byte-range request returns HTTP 206 after serving the packaged directory.
   - Check for missing assets, broken paths, console errors, navigation failures, and unusable mobile controls.
   - Confirm the ZIP can be extracted and served without the original project.
   - Do not deploy or publish anything.

6. Save the finished archive as:
   deliverables/${slug}-prospect-site.zip

Also provide a short packaging report with the ZIP path and size, entry point, included file count, major assets, exclusions, external dependencies, validation results, and remaining import risks.

Do not stop after creating the ZIP. Complete validation and correct packaging problems you find. Ask me only if the production entry point cannot be determined safely.`;
}
